import {OrganizationUserActions, OrganizationUserActionTypes} from '../actions/organization-user.actions';
import {OrganizationUser, SelfOrganizationUser} from '../models/organization-user';
import {ErrorPayload} from '../models/general';

export interface State {
  entities: { [email: string]: OrganizationUser };
  self: OrganizationUser;
  isGetOrganizationUsersLoading: boolean;
  isGetOrganizationUsersLoaded: boolean;
  getOrganizationUsersError: string | null;
  isGetSelfOrganizationUserLoading: boolean;
  isGetSelfOrganizationUserLoaded: boolean;
  getSelfOrganizationUserError: string | null;
  isPostOrganizationInvitationLoading: boolean;
  isPostOrganizationInvitationLoaded: boolean;
  postOrganizationInvitationError: string | null;
  isPostGroupInvitationLoading: boolean;
  isPostGroupInvitationLoaded: boolean;
  postGroupInvitationError: string | null;
  isDeletePendingOrganizationUserLoading: boolean;
  isDeletePendingOrganizationUserLoaded: boolean;
  deletePendingOrganizationUserError: string | null;
  isDeletePendingOrganizationMemberLoading: boolean;
  isDeletePendingOrganizationMemberLoaded: boolean;
  deletePendingOrganizationMemberError: string | null;
}

export const initialState: State = {
  entities: {},
  self: null,
  isGetOrganizationUsersLoading: false,
  isGetOrganizationUsersLoaded: false,
  getOrganizationUsersError: null,
  isGetSelfOrganizationUserLoading: false,
  isGetSelfOrganizationUserLoaded: false,
  getSelfOrganizationUserError: null,
  isPostOrganizationInvitationLoading: false,
  isPostOrganizationInvitationLoaded: false,
  postOrganizationInvitationError: null,
  isPostGroupInvitationLoading: false,
  isPostGroupInvitationLoaded: false,
  postGroupInvitationError: null,
  isDeletePendingOrganizationUserLoading: false,
  isDeletePendingOrganizationUserLoaded: false,
  deletePendingOrganizationUserError: null,
  isDeletePendingOrganizationMemberLoading: false,
  isDeletePendingOrganizationMemberLoaded: false,
  deletePendingOrganizationMemberError: null
};

export function reducer(state = initialState, action: OrganizationUserActions): State {
  switch (action.type) {

    case OrganizationUserActionTypes.GetOrganizationUsers: {
      return {
        ...state,
        isGetOrganizationUsersLoading: true,
        isGetOrganizationUsersLoaded: false,
        getOrganizationUsersError: null
      };
    }

    case OrganizationUserActionTypes.GetOrganizationUsersComplete: {
      const organizationUsers: OrganizationUser[] = action.payload;
      const entities = organizationUsers.reduce((acc: { [id: string]: OrganizationUser }, organizationUser: OrganizationUser) => {
        return {
          ...acc,
          [organizationUser.email]: organizationUser
        };
      }, {});

      return {
        ...state,
        entities,
        isGetOrganizationUsersLoading: false,
        isGetOrganizationUsersLoaded: true
      };
    }

    case OrganizationUserActionTypes.GetOrganizationUsersError: {
      const errorPayload: ErrorPayload = action.payload;
      return {
        ...state,
        isGetOrganizationUsersLoading: false,
        isGetOrganizationUsersLoaded: true,
        getOrganizationUsersError: errorPayload.errorMessage
      };
    }

    case OrganizationUserActionTypes.GetSelfOrganizationUser: {
      return {
        ...state,
        isGetSelfOrganizationUserLoading: true,
        isGetSelfOrganizationUserLoaded: false,
        getSelfOrganizationUserError: null
      };
    }

    case OrganizationUserActionTypes.GetSelfOrganizationUserComplete: {
      const self: SelfOrganizationUser = action.payload;
      return {
        ...state,
        self,
        isGetSelfOrganizationUserLoading: false,
        isGetSelfOrganizationUserLoaded: true
      };
    }

    case OrganizationUserActionTypes.GetSelfOrganizationUserError: {
      const errorPayload: ErrorPayload = action.payload;
      return {
        ...state,
        isGetSelfOrganizationUserLoading: false,
        isGetSelfOrganizationUserLoaded: true,
        getSelfOrganizationUserError: errorPayload.errorMessage
      };
    }

    case OrganizationUserActionTypes.PostOrganizationInvitation: {
      return {
        ...state,
        isPostOrganizationInvitationLoading: true,
        isPostOrganizationInvitationLoaded: false,
        postOrganizationInvitationError: null
      };
    }

    case OrganizationUserActionTypes.PostOrganizationInvitationComplete: {
      return {
        ...state,
        isPostOrganizationInvitationLoading: false,
        isPostOrganizationInvitationLoaded: true
      };
    }

    case OrganizationUserActionTypes.PostOrganizationInvitationError: {
      const errorPayload: ErrorPayload = action.payload;
      return {
        ...state,
        isPostOrganizationInvitationLoading: false,
        postOrganizationInvitationError: errorPayload.errorMessage
      };
    }

    case OrganizationUserActionTypes.PostGroupInvitation: {
      return {
        ...state,
        isPostGroupInvitationLoading: true,
        isPostGroupInvitationLoaded: false,
        postGroupInvitationError: null
      };
    }

    case OrganizationUserActionTypes.PostGroupInvitationComplete: {
      return {
        ...state,
        isPostGroupInvitationLoading: false,
        isPostGroupInvitationLoaded: true,
      };
    }

    case OrganizationUserActionTypes.PostGroupInvitationError: {
      const errorPayload: ErrorPayload = action.payload;

      return {
        ...state,
        isPostGroupInvitationLoading: false,
        postGroupInvitationError: errorPayload.errorMessage
      };
    }

    case OrganizationUserActionTypes.DeletePendingOrganizationUser: {
      return {
        ...state,
        isDeletePendingOrganizationUserLoading: true,
        isDeletePendingOrganizationUserLoaded: false,
        deletePendingOrganizationUserError: null
      };
    }

    case OrganizationUserActionTypes.DeletePendingOrganizationUserComplete: {
      return {
        ...state,
        isDeletePendingOrganizationUserLoading: false,
        isDeletePendingOrganizationUserLoaded: true
      };
    }

    case OrganizationUserActionTypes.DeletePendingOrganizationUserError: {
      const errorPayload: ErrorPayload = action.payload;
      return {
        ...state,
        isDeletePendingOrganizationUserLoading: false,
        deletePendingOrganizationUserError: errorPayload.errorMessage
      };
    }

    case OrganizationUserActionTypes.DeletePendingOrganizationMember: {
      return {
        ...state,
        isDeletePendingOrganizationMemberLoading: true,
        isDeletePendingOrganizationMemberLoaded: false,
        deletePendingOrganizationMemberError: null
      };
    }

    case OrganizationUserActionTypes.DeletePendingOrganizationMemberComplete: {
      return {
        ...state,
        isDeletePendingOrganizationMemberLoading: false,
        isDeletePendingOrganizationMemberLoaded: true
      };
    }

    case OrganizationUserActionTypes.DeletePendingOrganizationMemberError: {
      const errorPayload: ErrorPayload = action.payload;
      return {
        ...state,
        isDeletePendingOrganizationMemberLoading: false,
        deletePendingOrganizationMemberError: errorPayload.errorMessage
      };
    }

    default:
      return state;
  }
}

export const organizationUserEntities = (state: State) => state.entities;
export const selfOrganizationUser = (state: State) => state.self;
export const isGetOrganizationUsersLoading = (state: State) => state.isGetOrganizationUsersLoading;
export const isGetOrganizationUsersLoaded = (state: State) => state.isGetOrganizationUsersLoaded;
export const getOrganizationUsersError = (state: State) => state.getOrganizationUsersError;
export const isGetSelfOrganizationUserLoading = (state: State) => state.isGetSelfOrganizationUserLoading;
export const isGetSelfOrganizationUserLoaded = (state: State) => state.isGetSelfOrganizationUserLoaded;
export const getSelfOrganizationUserError = (state: State) => state.getSelfOrganizationUserError;
export const isPostOrganizationInvitationLoading = (state: State) => state.isPostOrganizationInvitationLoading;
export const isPostOrganizationInvitationLoaded = (state: State) => state.isPostOrganizationInvitationLoaded;
export const postOrganizationInvitationError = (state: State) => state.postOrganizationInvitationError;
export const isPostGroupInvitationLoading = (state: State) => state.isPostGroupInvitationLoading;
export const isPostGroupInvitationLoaded = (state: State) => state.isPostGroupInvitationLoaded;
export const postGroupInvitationError = (state: State) => state.postGroupInvitationError;
export const isDeletePendingOrganizationUserLoading = (state: State) => state.isDeletePendingOrganizationUserLoading;
export const isDeletePendingOrganizationUserLoaded = (state: State) => state.isDeletePendingOrganizationUserLoaded;
export const deletePendingOrganizationUserError = (state: State) => state.deletePendingOrganizationUserError;
export const isDeletePendingOrganizationMemberLoading = (state: State) => state.isDeletePendingOrganizationMemberLoading;
export const isDeletePendingOrganizationMemberLoaded = (state: State) => state.isDeletePendingOrganizationMemberLoaded;
export const deletePendingOrganizationMemberError = (state: State) => state.deletePendingOrganizationMemberError;
